const _excluded = ["selectedLegalTerms", "legalTerms", "showViewTosButton", "isReadOnly", "onChange", "onRemove", "onUpload"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import { useEffect, useRef, useState } from 'react';
import { Plus, TrashFill18 } from 'seagull/icons';
import { IconButton, MenuItem, Spinner, TextButton, TextField, Typography, useOverlayContext } from 'seagull';
import { useAppConfig } from "../../store/store-context";
import { legalTermsModel } from "../../models";
import { useIoContext, useStoreActions } from "../../contexts";
import RichTextViewerModal from "../RichTextViewerModal";
import { sendErrReport } from "../../infra/utils";
import styles from "./LegalTermsDropdown.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const loadingValue = 'loading';
const removePDFsuffix = filename => filename.replace(/\.pdf$/i, '');
const makeMapFromLegalTermsArray = docs => new Map(docs.slice().sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).map(doc => [doc.id, doc]));
export default function LegalTermsDropdown(_ref) {
  var _props$dataTestid;
  let {
      selectedLegalTerms,
      legalTerms,
      showViewTosButton = false,
      isReadOnly,
      onChange: _onChange,
      onRemove,
      onUpload
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const overlayContext = useOverlayContext();
  const {
    openNewTab,
    uploadFileToCloudStorage
  } = useIoContext();
  const [showModal, setShowModal] = useState(false);
  const [internalSelectedLegalTerms, setInternalSelectedLegalTerms] = useState(selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id);
  const [internalLegalTerms, setInternalLegalTerms] = useState(() => makeMapFromLegalTermsArray(legalTerms));
  const [isLoading, setIsLoading] = useState(true);
  const {
    termsAndConditionsPDFSizeLimitInMegabytes: pdfSizeLimit,
    generateUniqueId
  } = useAppConfig();
  const {
    api
  } = useStoreActions();
  const fileInputRef = useRef(null);
  useEffect(() => {
    setIsLoading(!selectedLegalTerms || internalLegalTerms.size === 0);
  }, [selectedLegalTerms, internalLegalTerms]);
  useEffect(() => {
    setInternalLegalTerms(makeMapFromLegalTermsArray(legalTerms));
  }, [legalTerms]);
  useEffect(() => {
    setInternalSelectedLegalTerms(selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id);
  }, [selectedLegalTerms]);
  const testPrefix = (_props$dataTestid = props['data-testid']) !== null && _props$dataTestid !== void 0 ? _props$dataTestid : 'legal-terms-uploader';
  const handlePDFUpload = async e => {
    e.preventDefault();
    try {
      const file = e.target.files && e.target.files[0];
      if (!file) {
        throw new PDFError('An uknown error has occurred.', 'unknown');
      }
      if (file.type !== 'application/pdf') {
        throw new PDFError('File is not a valid PDF.', 'invalid-pdf');
      }
      if (file.size > pdfSizeLimit * 1024 * 1024) {
        throw new PDFError('Selected file is too large. Please select a smaller one.', 'too-large');
      }
      setIsLoading(true);
      const {
        fileId,
        url
      } = await api.business.getLegalTermsUploadURL();
      await uploadFileToCloudStorage(url, file, {
        'content-type': 'application/pdf'
      });
      const legalTermsDoc = legalTermsModel.makePdfLegalTerms({
        name: file.name,
        pdfUniqueName: fileId
      }, generateUniqueId);
      await api.business.createLegalTerms(legalTermsDoc);
      onUpload();
      setInternalLegalTerms(makeMapFromLegalTermsArray([...internalLegalTerms.values(), legalTermsDoc]));
      setInternalSelectedLegalTerms(legalTermsDoc.id);
      _onChange(legalTermsDoc.id);
    } catch (error) {
      const isPdfError = error instanceof PDFError;
      const message = isPdfError ? error.message : 'An unknown error has occurred.';
      if (!isPdfError) {
        sendErrReport(error);
      }
      const id = isPdfError ? error.id : 'unknown';
      overlayContext.showErrorToast(message, "".concat(id, "-error-toast"));
      setInternalSelectedLegalTerms(selectedLegalTerms === null || selectedLegalTerms === void 0 ? void 0 : selectedLegalTerms.id);
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  async function viewLegalTerms() {
    if (internalSelectedLegalTerms) {
      const selectedLegalTerms = internalLegalTerms.get(internalSelectedLegalTerms);
      if (selectedLegalTerms !== null && selectedLegalTerms !== void 0 && selectedLegalTerms.pdfUniqueName) {
        const url = await api.business.getLegalTermsDownloadURL(selectedLegalTerms.pdfUniqueName);
        if (url) {
          openNewTab(url);
        }
      } else {
        setShowModal(true);
      }
    }
  }
  return /*#__PURE__*/_jsxs("div", {
    className: styles.root,
    "data-testid": testPrefix,
    children: [/*#__PURE__*/_jsx("input", {
      ref: fileInputRef,
      type: "file",
      accept: ".pdf",
      onChange: handlePDFUpload,
      "data-testid": testPrefix + '-dropdown-file-input',
      hidden: true
    }), /*#__PURE__*/_jsxs(TextField, _objectSpread(_objectSpread({}, props), {}, {
      select: true,
      value: isLoading ? loadingValue : internalSelectedLegalTerms,
      onChange: _ref2 => {
        let {
          target: {
            value
          }
        } = _ref2;
        const newValue = internalLegalTerms.get(value);
        if (!newValue || newValue.id === 'upload-new') return;
        setInternalSelectedLegalTerms(newValue.id);
        _onChange(newValue.id);
      },
      "data-testid": testPrefix + '-dropdown',
      disabled: isLoading || isReadOnly,
      SelectProps: {
        renderValue: value => {
          if (value === loadingValue) {
            return /*#__PURE__*/_jsxs(Typography, {
              className: styles.loadingRow,
              skin: "secondary",
              children: [/*#__PURE__*/_jsx(Spinner, {
                className: styles.spinner,
                size: 24,
                "data-testid": testPrefix + '-dropdown-spinner'
              }), ' ', "Loading\u2026"]
            });
          }
          const legalTerm = internalLegalTerms.get(value);
          return legalTerm ? removePDFsuffix(legalTerm.name) : '';
        }
      },
      children: [isLoading && /*#__PURE__*/_jsx(MenuItem, {
        value: loadingValue
      }), /*#__PURE__*/_jsx(MenuItem, {
        value: "upload-new",
        "data-testid": testPrefix + '-dropdown-option-upload-new',
        className: styles.uploadPdfRow,
        onClick: () => {
          var _fileInputRef$current;
          return fileInputRef === null || fileInputRef === void 0 || (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click();
        },
        children: /*#__PURE__*/_jsxs(TextButton, {
          as: "p",
          underline: "none",
          prefixIcon: /*#__PURE__*/_jsx(Plus, {}),
          children: ["Upload", ' ', /*#__PURE__*/_jsx(Typography, {
            as: "span",
            skin: "secondary",
            variant: "paragraph2",
            children: "(PDF smaller than 50MB)"
          })]
        })
      }), Array.from(internalLegalTerms.values()).map((_ref3, idx) => {
        let {
          id,
          name
        } = _ref3;
        return /*#__PURE__*/_jsx(MenuItem, {
          value: id,
          "data-testid": testPrefix + '-dropdown-option-' + idx,
          children: /*#__PURE__*/_jsxs("div", {
            className: styles.menuItem,
            children: [removePDFsuffix(name), onRemove && internalLegalTerms.size > 1 && /*#__PURE__*/_jsx(IconButton, {
              noPadding: true,
              size: "small",
              skin: "error",
              "data-testid": "".concat(testPrefix, "-dropdown-option-").concat(idx, "-rm"),
              onClick: e => {
                e.stopPropagation();
                onRemove(id);
              },
              children: /*#__PURE__*/_jsx(TrashFill18, {})
            })]
          })
        }, id);
      })]
    })), showViewTosButton && /*#__PURE__*/_jsxs("div", {
      className: styles.viewTosButton,
      children: [/*#__PURE__*/_jsx(TextButton, {
        type: "button",
        "data-testid": testPrefix + '-view-tos-button',
        onClick: viewLegalTerms,
        children: "View terms & conditions"
      }), showModal && selectedLegalTerms && /*#__PURE__*/_jsx(RichTextViewerModal, {
        show: true,
        title: removePDFsuffix(selectedLegalTerms.name),
        text: selectedLegalTerms.richTextContent,
        onCancel: () => setShowModal(false)
      })]
    })]
  });
}
class PDFError extends Error {
  constructor(message, id) {
    super(message);
    this.id = id;
  }
}